import { supabase } from './supabase';

export interface PortfolioValue {
  totalValue: number;
  itemCount: number;
  usesRestorationValuations?: boolean;
}

/** Authenticated S1b current portfolio: registered items use qualified reads;
 * unregistered items retain V3's display rules. No raw fallback when RPC is absent. */
export async function getVerifiedPortfolioValue(userId: string): Promise<PortfolioValue | null> {
  try {
    const { data, error } = await supabase
      .rpc('restoration_portfolio_value');

    if (error) {
      console.error('Error fetching verified portfolio value:', error);
      return null;
    }

    if (!data || data.length === 0) {
      return { totalValue: 0, itemCount: 0 };
    }

    return {
      totalValue: Number(data[0].total_value) || 0,
      itemCount: data[0].item_count || 0,
      usesRestorationValuations: (data[0].registered_item_count ?? 0) > 0,
    };
  } catch (error) {
    console.error('Error in getVerifiedPortfolioValue:', error);
    return null;
  }
}

export interface PortfolioValuationSummary {
  itemCount: number;
  valuedCount: number;
  legacyCount: number;
  unvaluedCount: number;
  pendingCount: number;
  identityUnresolvedCount: number;
  identityPendingCount: number;
}

/**
 * V3 P6 (A5 / A25): how many of the user's owned items show no value, and why. Reads
 * get_portfolio_valuation_summary (SECURITY INVOKER: RLS limits it to the caller's items).
 */
export async function getPortfolioValuationSummary(
  userId: string,
): Promise<PortfolioValuationSummary | null> {
  try {
    const { data, error } = await (supabase as any).rpc('get_portfolio_valuation_summary', {
      p_user_id: userId,
    });
    if (error || !data || data.length === 0) {
      if (error) console.error('Error fetching portfolio valuation summary:', error);
      return null;
    }
    const row = data[0];
    return {
      itemCount: row.item_count ?? 0,
      valuedCount: row.valued_count ?? 0,
      legacyCount: row.legacy_count ?? 0,
      unvaluedCount: row.unvalued_count ?? 0,
      pendingCount: row.pending_count ?? 0,
      identityUnresolvedCount: row.identity_unresolved_count ?? 0,
      identityPendingCount: row.identity_pending_count ?? 0,
    };
  } catch (error) {
    console.error('Error in getPortfolioValuationSummary:', error);
    return null;
  }
}

export interface PortfolioHistoryPoint { snapshot_date: string; total_value: number; item_count: number; }
/** Past V3 snapshots exclude registered items and cannot substantiate their change. */
export function portfolioHistoryWithLiveValue(history: PortfolioHistoryPoint[], value: PortfolioValue | null): PortfolioHistoryPoint[] {
  return value?.usesRestorationValuations ? [] : history;
}
